// src/encounter/services/use-save-assessment.ts
// Save the encounter Assessment & Plan (lot-aware vaccine admin, PROD-1227).
//   POST /api/Patients/CreateAssessment        — when the assessment has no identifier yet
//   PUT  /api/Patients/Assessment?Id={id}      — when it already exists (add/update vaccination)
// Mirrors the Flutter datasource (createPatientAssessment branches on identifier). Curl-verified
// 2026-06-18. The chosen lotNo/expiryDate ride the saved vaccination → on sign-lock the producer
// (PROD-1221) emits them → Inventory deducts that exact lot. api-client injects org/user headers.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { PatientAssessment } from '@/types/assessment-api';
import { useNoteWrite } from './note-session';

/**
 * Save an assessment — branches POST (create) vs PUT (update) on `body.identifier`, like Flutter.
 * Returns the assessment's IDENTIFIER (string), NOT the full document: Flutter's CreateAssessment
 * echoes just the new id string, and PUT echoes the document — so we normalize to the id (the only
 * field the caller needs to switch POST→PUT). The full server doc (with server-assigned child ids)
 * is reconciled by the caller from the invalidated assessment-query refetch, NOT from this response
 * — re-hydrating the canvas from a thin id-string response would wipe it (PROD-1297 live smoke).
 * Invalidates the encounter's assessment query so the A&P re-reads.
 */
export function useSaveAssessment() {
  const noteWrite = useNoteWrite();
  const qc = useQueryClient();
  return useMutation<string, unknown, PatientAssessment>({
    mutationFn: (body) =>
      noteWrite(async () => {
        if (body.identifier) {
          const doc = await put<PatientAssessment | string>(
            `/api/Patients/Assessment?Id=${encodeURIComponent(body.identifier)}`,
            body,
          );
          return (typeof doc === 'string' ? doc : doc?.identifier) ?? body.identifier;
        }
        // CreateAssessment echoes the new identifier as a bare string.
        return await post<string>('/api/Patients/CreateAssessment', body);
      }),
    onSuccess: (_id, body) => {
      qc.invalidateQueries({ queryKey: ['assessment', body.subject, body.encounter] });
      /*
       * ALSO refresh the patient summary — the encounter's top band reads
       * `usePatientSummary(patientId)` (`['patient-summary', patientId]`), and an A/P save is
       * exactly the thing that changes it: a diagnosis joins the problem list and a medication
       * order joins active medications. In the live Flutter app the band moves from
       * "ACTIVE PROBLEMS 45 / MEDICATIONS 0" to "46 / 1 (Paracetamol + codeine…)" once the plan
       * is placed; ours could not, because only the assessment query was invalidated, so the
       * band kept serving the counts it loaded when the encounter opened.
       *
       * `body.subject` is the patient id — the same key `usePatientSummary` builds. When the
       * backend has not promoted the rows yet (the DS card's "Joins problem list at sign"
       * disclosure says promotion happens at sign, not at save) the refetch simply returns what
       * it returned before, so this is safe either way: the band is never stale, and never
       * shows a promotion that has not happened.
       */
      if (body.subject) qc.invalidateQueries({ queryKey: ['patient-summary', body.subject] });
      /*
       * AND the wrap-up Pending orders (Order to Invoice). Billing builds that list live from this
       * plan, so a lab or procedure placed here belongs in it at once. The design v2 frame keeps
       * the wrap-up mounted next to the A&P, so no mount refetch fires; without this line a new
       * lab stayed off the list until something else re-read it (Andrew, 8 Oct). Prefix key:
       * the invoice id is not known here.
       */
      qc.invalidateQueries({ queryKey: ['invoice', 'orders'] });
    },
  });
}
